import React, { useEffect, useState } from "react";
import Icon from "@mdi/react";
import Select, { SingleValue } from "react-select";
import {
  mdiMapMarker,
  mdiClose,
  mdiWarehouse,
  mdiCalendar,
  mdiPackageVariant,
  mdiBarcodeScan,
  mdiCheckCircle,
  mdiEye,
  mdiAttachment,
  mdiAlertCircleOutline,
  mdiSync,
} from "@mdi/js";
import {
  StockTransferSubtype,
  WarehouseOutwardModel,
  WarehouseOutwardStatus,
  LogisticPartner,
  LOGISTIC_PARTNER_OPTIONS,
  splitLogisticPartner,
} from "../model/warehouse_outward_model";
import WarehouseSearchField from "../../../../erp/inventory/warehouse/warehouse_search_field";
import { WarehouseModel } from "../../../../erp/inventory/models/warehouse_model";
import { fetchPut } from "../../../../service/api_client";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import DeviceAllocationModal from "./device_allocation_modal";
import RejectSTModal from "../finance/RejectSTModal";
import DeviceIdsViewer from "../common-components/DeviceIdsViewer";
import { InwardAttachmentUpload } from "../inward/InwardAttachmentUpload";
import LoadingWidget from "../../../../context_providers/modal/loader";

import {
  getOutwardDestinationAddress,
  getOutwardDestinationLabel,
} from "../warehouse_helper";
import { GetAvailableQty } from "../warehouse_module_repo";
import { getOrgNamespaceApi } from "../../../../service/repos/cms/org_repo";
import ReplacementItemsModal from "./ReplacementItemsModal";

type Props = {
  open: boolean;
  stage: WarehouseOutwardStatus | undefined;
  outward: WarehouseOutwardModel;
  onClose: () => void;
  onSuccess?: () => void;
  onRefresh?: () => void;
};

type LogisticOption = { label: string; value: string };

// Logistic partner picker: a dropdown of the supported carriers, revealing a
// free-text field only when "Others" is chosen. The effective partner string
// is reported through onChange (the "Others" text when Others is selected).
const LogisticPartnerSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  resetKey?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}> = ({ value, onChange, resetKey, disabled, className, id }) => {
  const [selection, setSelection] = useState(
    () => splitLogisticPartner(value).selection,
  );
  const [otherValue, setOtherValue] = useState(
    () => splitLogisticPartner(value).otherValue,
  );

  useEffect(() => {
    const split = splitLogisticPartner(value);
    setSelection(split.selection);
    setOtherValue(split.otherValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const isOther = selection === LogisticPartner.OTHERS;

  return (
    <div className={className} id={id}>
      <Select<LogisticOption, false>
        placeholder="Select logistic partner"
        options={LOGISTIC_PARTNER_OPTIONS}
        value={selection ? { label: selection, value: selection } : null}
        isClearable
        isDisabled={disabled}
        onChange={(option: SingleValue<LogisticOption>) => {
          const next = option?.value || "";
          setSelection(next);
          if (next === LogisticPartner.OTHERS) {
            onChange(otherValue.trim());
          } else {
            setOtherValue("");
            onChange(next);
          }
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base) => ({
            ...base,
            minHeight: "40px",
            borderColor: "#d1d5db",
            boxShadow: "none",
          }),
        }}
      />
      {isOther && (
        <input
          id={id ? `${id}-other-input` : undefined}
          type="text"
          className="w-full mt-2 h-10 border border-gray-300 rounded-md px-3 text-sm"
          placeholder="Enter logistic partner name"
          value={otherValue}
          disabled={disabled}
          onChange={(e) => {
            setOtherValue(e.target.value);
            onChange(e.target.value.trim());
          }}
        />
      )}
    </div>
  );
};

const OutwardStageModal: React.FC<Props> = ({
  open,
  stage,
  outward: outwardProp,
  onClose,
  onSuccess,
  onRefresh,
}) => {
  const { showToast } = useToast();
  const [outward, setOutward] = useState(outwardProp);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const isInternalST =
    outward?.type === "ST" && (outward as any).is_internal === true;
  const [fromWarehouse, setFromWarehouse] = useState<WarehouseModel | null>(
    null,
  );
  const [logisticPartner, setLogisticPartner] = useState(
    outward.shipping_details?.logistic_partner || "",
  );
  const [trackingId, setTrackingId] = useState(
    outward.shipping_details?.tracking_id || "",
  );

  const [itemDevices, setItemDevices] = useState<Record<string, string[]>>({});
  const [deviceAllocationOpen, setDeviceAllocationOpen] = useState(false);
  const [selectedItemCode, setSelectedItemCode] = useState<string>("");
  const [selectedItemQty, setSelectedItemQty] = useState(0);
  const [selectedFromWarehouse, setSelectedFromWarehouse] =
    useState<string>("");
  const [deliveryAttachments, setDeliveryAttachments] = useState<File[]>([]);
  const [deliveryRemarks, setDeliveryRemarks] = useState("");
  const [warehouseStock, setWarehouseStock] = useState<Record<string, number>>(
    {},
  );
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failedDevices, setFailedDevices] = useState<
    { deviceId: string; reason: string }[]
  >([]);
  const [pickedQty, setPickedQty] = useState<Record<string, number>>({});
  const [failedPickingItems, setFailedPickingItems] = useState<
    { itemCode: string; reason: string }[]
  >([]);
  const [namespaces, setNameSpace] = useState<
    {
      name: string;
      namespace: string;
    }[]
  >([]);
  const [orgNamespace, setOrgNamespace] = useState<{
    org_name: string;
    org_namespace: string;
  }>({
    org_name: "",
    org_namespace: "",
  });
  type OrgOption = {
    label: string;
    value: {
      name: string;
      namespace: string;
    };
  };

  useEffect(() => {
    if (!open) return;
    setSyncMessage(null);
    setLogisticPartner(outward.shipping_details?.logistic_partner || "");
    setTrackingId(outward.shipping_details?.tracking_id || "");

    // Pre-select warehouse if already assigned on the outward or order
    const preWhId =
      (outward as any)?.from_warehouse_id ||
      (outward as any)?.warehouse_id ||
      "";
    const preWhName =
      (outward as any)?.from_warehouse_name ||
      (outward as any)?.warehouse_name ||
      "";
    if (preWhId) {
      const preWh = { id: preWhId, name: preWhName } as WarehouseModel;
      setFromWarehouse(preWh);
      // Fetch stock for pre-selected warehouse
      handleFromWarehouseSelect(preWh);
    }

    const preOrgNamespace =
      (outward as any)?.org?.namespace || (outward as any)?.org_namespace || "";
    const preOrgName =
      (outward as any)?.org?.name || (outward as any)?.org_name || "";
    if (preOrgNamespace) {
      setOrgNamespace({
        org_name: preOrgName,
        org_namespace: preOrgNamespace,
      });
    }
    // Deliberately keyed on open + outward.id only: re-initialising on every
    // shipping_details change would wipe the post-sync banner and refetch stock.
  }, [open, outward.id]);

  useEffect(() => {
    getOrgNamespaceApi()
      .then((val) => {
        if (val.success) {
          console.log(val.data);

          setNameSpace(val.data);
        } else alert(val.error);
      })
      .catch((e) => alert(e.message));
  }, []);

  if (!open) return null;

  const getTotalValue = (outward: WarehouseOutwardModel): number =>
    outward.items?.reduce(
      (sum, i) => sum + (i.itemPrice || 0) * i.requestedQty,
      0,
    ) ?? 0;

  const formatDate = (value?: Date | string): string => {
    if (!value) return "-";
    const d = typeof value === "string" ? new Date(value) : value;
    return isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
  };

  const formatAddress = (addr?: any): string => {
    if (!addr) return "-";

    return [
      addr.line1,
      addr.line2,
      addr.line3,
      addr.city,
      addr.state,
      addr.country,
      addr.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  };
  const hasFinishedGoods = (outward.items ?? []).some(
    (item: any) => item.subCategory === "Finished Goods",
  );

  const isWarehouseValid = outward.items.every((item) => {
    const available = warehouseStock[item.inventoryItemCode] ?? 0;
    return available >= item.requestedQty;
  });
  const orgOptions: OrgOption[] = [...namespaces]
    .sort((a, b) =>
      a.name
        ? a.name.localeCompare(b.name)
        : a.namespace.localeCompare(b.namespace),
    )
    .map((n) => ({
      label: n.name ?? n.namespace,
      value: {
        name: n.name,
        namespace: n.namespace,
      },
    }));
  const selectedOrgOption =
    orgOptions.find(
      (opt) => opt.value.namespace === orgNamespace.org_namespace,
    ) || null;

  const handleFromWarehouseSelect = async (data: WarehouseModel) => {
    setFromWarehouse(data);

    if (!outward?.items?.length) return;

    try {
      setStockLoading(true);
      setStockError(null);

      const res = await GetAvailableQty(data.id);

      if (!res?.success || !res?.data?.item_data) {
        setWarehouseStock({});
        return;
      }

      const stockMap: Record<string, number> = {};

      res.data.item_data.forEach((item: any) => {
        stockMap[item.item_code] = item.available_units;
      });

      setWarehouseStock(stockMap);
    } catch (err) {
      setStockError("Failed to fetch stock");
      setWarehouseStock({});
    } finally {
      setStockLoading(false);
    }
  };

  const handleAssignWarehouse = async () => {
    if (!fromWarehouse) {
      showToast({
        type: ToastType.warning,
        text: "Please select a warehouse",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const payload: any = {
        from_warehouse_id: fromWarehouse.id,
        from_warehouse_name: fromWarehouse.name,
        org_namespace: orgNamespace.org_namespace,
        org_name: orgNamespace.org_name,
      };
      if (logisticPartner.trim())
        payload.logistic_partner = logisticPartner.trim();
      if (trackingId.trim()) payload.tracking_id = trackingId.trim();

      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/assign-warehouse-SO/${outward.id}`,
        payload,
      );

      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to assign warehouse",
        });
        return;
      }
      showToast({
        type: ToastType.success,
        text: "Warehouse assigned successfully",
      });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err?.error || err?.message || "Failed to assign warehouse",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSODeviceAllocation = async () => {
    try {
      setIsSubmitting(true);
      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/device-allocation-SO/${outward.id}`,
        {
          items: outward.items.map((item) => ({
            inventoryItemCode: item.inventoryItemCode,
            deviceIds: itemDevices[item.inventoryItemCode],
          })),
        },
      );

      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to allocate devices",
        });
        return;
      }
      showToast({
        type: ToastType.success,
        text: "Devices allocated successfully",
      });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err?.error || err?.message || "Failed to allocate devices",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAllocateDevices = async () => {
    try {
      setIsSubmitting(true);
      setFailedDevices([]);

      const items = outward.items
        ?.filter((item) => itemDevices[item.inventoryItemCode]?.length > 0)
        .map((item) => ({
          inventoryItemCode: item.inventoryItemCode,
          deviceIds: itemDevices[item.inventoryItemCode],
          itemPrice: item.itemPrice,
        }));

      if (!items || items.length === 0) {
        showToast({
          type: ToastType.warning,
          text: "Please scan devices for at least one item",
        });
        setIsSubmitting(false);
        return;
      }

      const endpoint =
        outward.type === "ST"
          ? `update-ST/${outward.id}`
          : `update-SO/${outward.id}`;

      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/${endpoint}`,
        { items },
      );

      if (!res?.success) {
        if (Array.isArray(res?.failedDevices) && res.failedDevices.length) {
          setFailedDevices(res.failedDevices);
          showToast({
            type: ToastType.error,
            text:
              res.message ||
              "Some scanned devices are no longer available for allocation.",
          });
        } else {
          showToast({
            type: ToastType.error,
            text: res?.error || res?.message || "Failed to allocate devices",
          });
        }
        return;
      }

      setFailedDevices([]);

      showToast({
        type: ToastType.success,
        text: "Devices allocated successfully",
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Failed to allocate devices:", error);
      showToast({
        type: ToastType.error,
        text: "Failed to allocate devices",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmMaterialPicking = async () => {
    try {
      setIsSubmitting(true);
      setFailedPickingItems([]);

      const items = outward.items?.map((item) => ({
        inventoryItemCode: item.inventoryItemCode,
        pickedQty: pickedQty[item.inventoryItemCode] ?? item.requestedQty,
      }));

      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/confirm-material-picking/${outward.id}`,
        { items },
      );

      if (!res?.success) {
        if (Array.isArray(res?.failedItems) && res.failedItems.length) {
          setFailedPickingItems(res.failedItems);
          showToast({
            type: ToastType.error,
            text:
              res.message ||
              "Please enter the exact quantity for all items before confirming.",
          });
        } else {
          showToast({
            type: ToastType.error,
            text: res?.error || res?.message || "Failed to confirm picking",
          });
        }
        return;
      }

      setFailedPickingItems([]);
      showToast({
        type: ToastType.success,
        text: "Picking confirmed",
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Failed to confirm picking:", error);
      showToast({
        type: ToastType.error,
        text: "Failed to confirm picking",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkAsDispatched = async () => {
    const trimmedLogisticPartner = logisticPartner.trim();
    const trimmedTrackingId = trackingId.trim();
    const existingLogisticPartner =
      outward.shipping_details?.logistic_partner?.trim() || "";

    // Check if logistic partner is available either from input or existing data
    if (!trimmedLogisticPartner && !existingLogisticPartner) {
      showToast({
        type: ToastType.warning,
        text: "Please select logistic partner",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const payload: {
        logistic_partner: string;
        tracking_id?: string;
      } = {
        logistic_partner: trimmedLogisticPartner || existingLogisticPartner,
      };

      if (trimmedTrackingId || existingLogisticPartner) {
        payload.tracking_id = trimmedTrackingId || existingLogisticPartner;
      }

      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/mark-dispatched/${outward.id}`,
        payload,
      );

      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to mark as dispatched",
        });
        return;
      }

      showToast({
        type: ToastType.success,
        text: trimmedTrackingId
          ? "Marked as dispatched successfully"
          : "Logistic partner saved. Status moved to Tracking ID Pending.",
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Failed to mark as dispatched:", error);
      showToast({
        type: ToastType.error,
        text: "Failed to mark as dispatched",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTrackingIdAndDispatch = async () => {
    const trimmedLogisticPartner = logisticPartner.trim();
    const trimmedTrackingId = trackingId.trim();
    const existingLogisticPartner =
      outward.shipping_details?.logistic_partner?.trim() || "";

    if (!trimmedTrackingId) {
      showToast({
        type: ToastType.warning,
        text: "Tracking ID is required",
      });
      return;
    }

    if (!trimmedLogisticPartner && !existingLogisticPartner) {
      showToast({
        type: ToastType.error,
        text: "Logistic partner is missing. Please dispatch from Ready To Dispatch stage first.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/mark-dispatched/${outward.id}`,
        {
          logistic_partner: trimmedLogisticPartner || existingLogisticPartner,
          tracking_id: trimmedTrackingId,
        },
      );

      if (res.success) {
        showToast({
          type: ToastType.success,
          text: "Tracking ID updated and marked as dispatched",
        });
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || "Failed to update tracking ID",
        });
        return;
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Failed to update tracking ID:", error);
      showToast({
        type: ToastType.error,
        text: "Failed to update tracking ID",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkAsDelivered = async () => {
    try {
      setIsSubmitting(true);

      const formData = new FormData();
      deliveryAttachments.forEach((file) => {
        formData.append("attachments", file);
      });
      formData.append("logistic_partner", logisticPartner);
      formData.append("tracking_id", trackingId);
      formData.append("remarks", deliveryRemarks);

      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/mark-delivered/${outward.id}`,
        formData,
        { "Content-Type": "multipart/form-data" },
      );

      if (res.success) {
        showToast({
          type: ToastType.success,
          text: "Marked as delivered successfully",
        });
        onSuccess?.();
        onClose();
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || "Failed to mark as delivered",
        });
      }
    } catch (error: any) {
      console.error("Failed to mark as delivered:", error);
      showToast({
        type: ToastType.error,
        text: error.message || "Failed to mark as delivered",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSyncDelhivery = async () => {
    try {
      setIsSubmitting(true);
      const syncPayload: { tracking_id?: string } = {};
      if (trackingId.trim()) syncPayload.tracking_id = trackingId.trim();
      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/delhivery/sync/${outward.id}`,
        syncPayload,
      );

      if (res?.success) {
        const data = res.data ?? {};
        // Reflect the latest status inline; keep the modal open so the user
        // can review it and close when ready.
        setOutward((prev) => ({
          ...prev,
          shipping_details: {
            ...(prev.shipping_details ?? {}),
            logistic_partner:
              prev.shipping_details?.logistic_partner ||
              LogisticPartner.DELHIVERY,
            tracking_id:
              data.tracking_id ?? prev.shipping_details?.tracking_id ?? "",
            tracking_status:
              data.tracking_status ?? prev.shipping_details?.tracking_status,
            tracking_status_raw:
              data.tracking_status_raw ??
              prev.shipping_details?.tracking_status_raw,
            delhivery_synced_at: new Date().toISOString(),
            delhivery_sync_error: undefined,
          },
        }));
        setSyncMessage(res.message || "Delhivery status synced");
        showToast({
          type: ToastType.success,
          text: res.message || "Delhivery status synced",
        });
        // Refresh the underlying list in the background without closing.
        onRefresh?.();
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to sync Delhivery status",
        });
      }
    } catch (error: any) {
      console.error("Failed to sync Delhivery status:", error);
      showToast({
        type: ToastType.error,
        text: error.message || "Failed to sync Delhivery status",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Persist only the logistic partner / tracking id on an already-dispatched
  // outward, leaving the status untouched. Used when the carrier is switched to
  // Delhivery so the shipment joins the Delhivery sync-only delivery flow
  // instead of being marked delivered manually.
  const handleUpdateLogisticPartner = async () => {
    const trimmedLogisticPartner = logisticPartner.trim();
    if (!trimmedLogisticPartner) {
      showToast({
        type: ToastType.warning,
        text: "Please select logistic partner",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const payload: { logistic_partner: string; tracking_id?: string } = {
        logistic_partner: trimmedLogisticPartner,
      };
      if (trackingId.trim()) payload.tracking_id = trackingId.trim();

      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/update-shipping-details/${outward.id}`,
        payload,
      );

      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to update logistic partner",
        });
        return;
      }

      const data = res.data ?? {};
      // Reflect the change inline and keep the modal open so the user can now
      // sync the Delhivery status.
      setOutward((prev) => ({
        ...prev,
        shipping_details: {
          ...(prev.shipping_details ?? {}),
          logistic_partner:
            data.logistic_partner ||
            trimmedLogisticPartner ||
            (prev.shipping_details?.logistic_partner ?? ""),
          tracking_id:
            data.tracking_id ??
            (trackingId.trim() || prev.shipping_details?.tracking_id || ""),
        },
      }));
      showToast({
        type: ToastType.success,
        text: res.message || "Logistic partner updated",
      });
      onRefresh?.();
    } catch (error: any) {
      console.error("Failed to update logistic partner:", error);
      showToast({
        type: ToastType.error,
        text: error.message || "Failed to update logistic partner",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const storedIsDelhivery =
    outward.shipping_details?.logistic_partner === LogisticPartner.DELHIVERY;
  // The partner currently chosen in the modal (may differ from what is stored
  // if the user just switched it in the Dispatched stage).
  const selectedIsDelhivery =
    logisticPartner.trim() === LogisticPartner.DELHIVERY;
  // Show the Delhivery sync flow whenever the shipment is — or is being set to
  // — a Delhivery shipment.
  const isDelhivery = storedIsDelhivery || selectedIsDelhivery;
  // User picked Delhivery but it has not been persisted yet. Until it is, the
  // shipment must not be marked delivered manually; it only needs its partner
  // updated so it joins the Delhivery sync-only delivery flow.
  const pendingDelhiverySwitch = selectedIsDelhivery && !storedIsDelhivery;
  // Delhivery shipments can only be marked delivered once Delhivery itself
  // reports DELIVERED; any other status blocks the manual action.
  const isDelhiveryDelivered =
    (outward.shipping_details?.tracking_status_raw || "")
      .trim()
      .toUpperCase() === "DELIVERED";
  const blockManualDelivery =
    pendingDelhiverySwitch || (storedIsDelhivery && !isDelhiveryDelivered);

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      />

      {/* Modal */}
      <div
        id="warehouse-outward-stage-modal"
        role="dialog"
        aria-modal="true"
        className="fixed z-50 rounded-xl bg-white p-6 shadow-2xl border border-gray-200 wh-modal-xs"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "40rem",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div className="flex flex-col space-y-1.5 pb-4 border-b border-gray-100">
          <h2 className="text-xl font-bold flex items-center gap-3 text-gray-900">
            <span>{outward.type === "ST" ? outward.id : outward.order_id}</span>

            <span className="rounded-full bg-blue-100 text-blue-800 border border-blue-200 px-3 py-1 text-xs font-semibold">
              {outward.type}
            </span>

            {stage !== WarehouseOutwardStatus.WH_YET_TO_BE_ASSIGNED && (
              <span className="rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-3 py-1 text-xs font-medium">
                {outward.status}
              </span>
            )}
          </h2>
        </div>

        {/* BODY */}
        {stage === WarehouseOutwardStatus.WH_YET_TO_BE_ASSIGNED && (
          <div className="space-y-6 mt-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-900">
              <Icon path={mdiMapMarker} size={0.9} className="text-blue-600" />
              Assign Warehouse
            </h3>

            <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
              <p className="font-semibold text-gray-900 text-lg">
                {formatAddress(getOutwardDestinationAddress(outward))}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Total Value: ₹{getTotalValue(outward).toLocaleString()}
              </p>
            </div>

            {((outward as any).suggested_transport_mode ||
              (outward as any).warehouse_suggestion_remarks) && (
              <div className="rounded-lg bg-amber-50 p-4 border border-amber-200">
                <p className="text-sm font-semibold text-amber-800">
                  Approver Suggestion
                </p>
                {(outward as any).suggested_transport_mode && (
                  <p className="text-sm text-amber-900 mt-1">
                    Mode of Transport:{" "}
                    <span className="font-semibold">
                      {(outward as any).suggested_transport_mode}
                    </span>
                  </p>
                )}
                {(outward as any).warehouse_suggestion_remarks && (
                  <p className="text-sm text-amber-900 mt-1">
                    Remarks: {(outward as any).warehouse_suggestion_remarks}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700">
                Select Warehouse *
              </label>

              <WarehouseSearchField
                id="warehouse-outward-assign-warehouse-search"
                type=""
                value={fromWarehouse || undefined}
                placeholder="Select source warehouse"
                onSelect={handleFromWarehouseSelect}
              />
            </div>

            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700">
                Select Organization {hasFinishedGoods ? "*" : ""}
              </label>
              <Select<OrgOption, false>
                id="warehouse-outward-assign-org-select"
                inputId="namespace"
                placeholder="Search organization"
                options={orgOptions}
                value={selectedOrgOption}
                isSearchable
                isClearable
                isDisabled={!hasFinishedGoods}
                onChange={(option: SingleValue<OrgOption>) => {
                  if (!option) {
                    setOrgNamespace({ org_name: "", org_namespace: "" });
                    return;
                  }
                  setOrgNamespace({
                    org_name: option.value.name ?? option.value.namespace,
                    org_namespace: option.value.namespace,
                  });
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 60 }),
                  control: (base) => ({
                    ...base,
                    minHeight: "34px",
                    borderColor: "#d1d5db",
                    boxShadow: "none",
                  }),
                }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Logistic Partner
                </label>
                <LogisticPartnerSelect
                  id="warehouse-outward-assign-logistic-partner-select"
                  value={logisticPartner}
                  onChange={setLogisticPartner}
                  resetKey={open ? outward.id : ""}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Tracking ID
                </label>
                <input
                  id="warehouse-outward-assign-tracking-id-input"
                  type="text"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  className="w-full h-10 border rounded-md px-3"
                  placeholder="Enter tracking ID"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-gray-900">
                <Icon
                  path={mdiPackageVariant}
                  size={0.8}
                  className="text-orange-600"
                />
                Items
              </h3>

              {outward.items?.map((item) => {
                const devices = itemDevices[item.inventoryItemCode] || [];

                return (
                  <div
                    key={item.inventoryItemCode}
                    id={`warehouse-outward-assign-item-${item.inventoryItemCode}`}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {item.itemName}
                        </p>
                        <p className="text-sm text-gray-600">
                          {item.inventoryItemCode}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 text-sm gap-4">
                      <div>
                        <p className="text-gray-500">Ordered Qty</p>
                        <p className="font-semibold">{item.requestedQty}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Available Qty</p>
                        <p
                          className={`font-semibold ${
                            (warehouseStock[item.inventoryItemCode] ?? 0) <
                            item.requestedQty
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {stockLoading
                            ? "Checking..."
                            : (warehouseStock[item.inventoryItemCode] ?? 0)}
                        </p>
                      </div>
                    </div>

                    {devices.length > 0 && (
                      <div className="text-xs text-gray-600 break-all">
                        <DeviceIdsViewer
                          deviceIds={devices}
                          title="Scanned Devices"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {fromWarehouse && !stockLoading && !isWarehouseValid && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm font-semibold text-red-700">
                  Cannot assign warehouse — insufficient stock
                </p>
                <ul className="mt-1 space-y-0.5 text-sm text-red-600 list-disc list-inside">
                  {outward.items
                    .filter(
                      (item) =>
                        (warehouseStock[item.inventoryItemCode] ?? 0) <
                        item.requestedQty,
                    )
                    .map((item) => (
                      <li key={item.inventoryItemCode}>
                        {item.itemName} ({item.inventoryItemCode}): need{" "}
                        {item.requestedQty}, available{" "}
                        {warehouseStock[item.inventoryItemCode] ?? 0}
                      </li>
                    ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                id="warehouse-outward-assign-cancel-btn"
                onClick={onClose}
                className="flex-1 h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                id="warehouse-outward-assign-submit-btn"
                onClick={handleAssignWarehouse}
                disabled={
                  !fromWarehouse ||
                  stockLoading ||
                  !isWarehouseValid ||
                  isSubmitting
                }
                className={`flex-1 h-12 rounded-lg font-medium transition-all ${
                  !fromWarehouse ||
                  stockLoading ||
                  !isWarehouseValid ||
                  isSubmitting
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {isSubmitting ? "Assigning..." : "Assign Warehouse"}
              </button>
            </div>
          </div>
        )}

        {outward.type === "SO" &&
          stage === WarehouseOutwardStatus.DEVICE_ALLOCATION_PENDING && (
            <div className="space-y-6 mt-6">
              <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-900">
                <Icon
                  path={mdiMapMarker}
                  size={0.9}
                  className="text-blue-600"
                />
                Device Allocation
              </h3>

              <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
                <p className=" text-gray-800 mt-1">
                  From Warehouse: {outward.from_warehouse_name}
                </p>
                <p className="font-semibold text-gray-900 text-lg">
                  {formatAddress(getOutwardDestinationAddress(outward))}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Total Value: ₹{getTotalValue(outward).toLocaleString()}
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 text-gray-900">
                  <Icon
                    path={mdiPackageVariant}
                    size={0.8}
                    className="text-orange-600"
                  />
                  Items
                </h3>

                {outward.items?.map((item) => {
                  const devices = itemDevices[item.inventoryItemCode] || [];

                  return (
                    <div
                      key={item.inventoryItemCode}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {item.itemName}
                          </p>
                          <p className="text-sm text-gray-600">
                            {item.inventoryItemCode}
                          </p>
                        </div>

                        {item.subCategory === "Finished Goods" && (
                          <button
                            id={`warehouse-outward-so-scan-devices-${item.inventoryItemCode}`}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-100 text-purple-700 text-sm font-medium hover:bg-purple-200"
                            onClick={() => {
                              setSelectedItemCode(item.inventoryItemCode);
                              setSelectedItemQty(item.requestedQty);
                              setSelectedFromWarehouse(
                                outward.from_warehouse_id ?? "",
                              );
                              setDeviceAllocationOpen(true);
                            }}
                          >
                            <Icon path={mdiBarcodeScan} size={0.7} />
                            Scan Devices
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 text-sm">
                        <div>
                          <p className="text-gray-500">Ordered Qty</p>
                          <p className="font-semibold">{item.requestedQty}</p>
                        </div>

                        {item.subCategory === "Finished Goods" && (
                          <div>
                            <p className="text-gray-500">Scanned Devices</p>
                            <p className="font-semibold text-green-600">
                              {devices.length}
                            </p>
                          </div>
                        )}
                      </div>

                      {devices.length > 0 && (
                        <div className="text-xs text-gray-600 break-all">
                          {devices.join(", ")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  id="warehouse-outward-so-device-allocation-cancel-btn"
                  onClick={onClose}
                  className="flex-1 h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  id="warehouse-outward-so-device-allocation-submit-btn"
                  onClick={handleSODeviceAllocation}
                  disabled={isSubmitting}
                  className={`flex-1 h-12 rounded-lg font-medium transition-all ${
                    isSubmitting
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {isSubmitting ? "Allocating..." : "Allocate Devices"}
                </button>
              </div>
            </div>
          )}

        {outward.type === "ST" &&
          stage === WarehouseOutwardStatus.DEVICE_ALLOCATION_PENDING && (
            <div className="space-y-6 mt-6">
              {/* Meta */}
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div className="space-y-1">
                  <p className="text-gray-500 flex items-center gap-2">
                    <Icon
                      path={mdiWarehouse}
                      size={0.7}
                      className="text-blue-600"
                    />{" "}
                    From Warehouse
                  </p>
                  <p className="font-semibold text-gray-900">
                    {outward.from_warehouse_name}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-gray-500 flex items-center gap-2">
                    <Icon
                      path={mdiMapMarker}
                      size={0.7}
                      className="text-green-600"
                    />{" "}
                    Destination
                  </p>
                  <p className="font-semibold text-gray-900">
                    {getOutwardDestinationLabel(outward)}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-gray-500 flex items-center gap-2">
                    <Icon
                      path={mdiCalendar}
                      size={0.7}
                      className="text-purple-600"
                    />{" "}
                    Expected Date
                  </p>
                  <p className="font-semibold text-gray-900">
                    {outward.type === "ST" &&
                      outward.st_subtype === StockTransferSubtype.REGULAR &&
                      formatDate(outward.expected_date)}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-gray-500 flex items-center gap-2">
                    <Icon
                      path={mdiPackageVariant}
                      size={0.7}
                      className="text-orange-600"
                    />{" "}
                    Total Value
                  </p>
                  <p className="font-semibold text-gray-900 text-lg">
                    ₹{getTotalValue(outward).toLocaleString()}
                  </p>
                </div>
              </div>

              <hr className="border-gray-200" />

              {/* Items */}
              <div className="space-y-6">
                {/* Faulty Items */}
                {outward.st_subtype === StockTransferSubtype.REPLACEMENT && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-red-600 flex items-center gap-2">
                      <Icon path={mdiAlertCircleOutline} size={0.8} />
                      Faulty Items
                    </h3>

                    {outward.faulty_items?.map((item) => {
                      const devices = itemDevices[item.inventoryItemCode] || [];

                      return (
                        <div
                          key={item.inventoryItemCode}
                          className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3"
                        >
                          <div className="flex justify-between">
                            <div>
                              <p className="font-semibold">{item.itemName}</p>
                              <p className="text-sm">
                                {item.inventoryItemCode}
                              </p>
                            </div>

                            <p className="font-semibold">
                              Qty: {item.requestedQty}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Replacement / Normal Items */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold flex items-center gap-2 text-gray-900">
                      <Icon
                        path={mdiPackageVariant}
                        size={0.8}
                        className="text-orange-600"
                      />
                      Replacement Items
                    </h3>

                    {outward.st_subtype ===
                      StockTransferSubtype.REPLACEMENT && (
                      <button
                        id="warehouse-outward-st-replace-item-btn"
                        onClick={() => setShowReplacementModal(true)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                      >
                        Replace with another item
                      </button>
                    )}
                  </div>

                  {outward.items?.map((item) => {
                    const devices = itemDevices[item.inventoryItemCode] || [];

                    return (
                      <div
                        key={item.inventoryItemCode}
                        className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold">{item.itemName}</p>
                            <p className="text-sm text-gray-600">
                              {item.inventoryItemCode}
                            </p>
                          </div>

                          {item.subCategory === "Finished Goods" && (
                            <button
                              id={`warehouse-outward-st-scan-devices-${item.inventoryItemCode}`}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 text-sm font-medium hover:bg-blue-200"
                              onClick={() => {
                                setSelectedItemCode(item.inventoryItemCode);
                                setSelectedItemQty(item.requestedQty);
                                setDeviceAllocationOpen(true);
                              }}
                            >
                              <Icon path={mdiBarcodeScan} size={0.7} />
                              Scan Devices
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 text-sm">
                          <div>
                            <p className="text-gray-500">Qty</p>
                            <p className="font-semibold">{item.requestedQty}</p>
                          </div>

                          {item.subCategory === "Finished Goods" && (
                            <div>
                              <p className="text-gray-500">Scanned</p>
                              <p className="font-semibold text-green-600">
                                {devices.length}
                              </p>
                            </div>
                          )}
                        </div>

                        <DeviceIdsViewer
                          deviceIds={devices}
                          title="Scanned Devices"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {failedDevices.length > 0 && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-4 space-y-2">
                  <p className="font-semibold text-red-700 flex items-center gap-2">
                    <Icon path={mdiAlertCircleOutline} size={0.8} />
                    {failedDevices.length} device
                    {failedDevices.length > 1 ? "s" : ""} could not be allocated
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 max-h-48 overflow-y-auto">
                    {failedDevices.map((f) => (
                      <li
                        key={f.deviceId}
                        className="flex justify-between gap-3 border-b border-red-100 pb-1"
                      >
                        <span className="font-mono">{f.deviceId}</span>
                        <span className="text-right">{f.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  id="warehouse-outward-st-device-allocation-cancel-btn"
                  onClick={onClose}
                  className="flex-1 h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  id="warehouse-outward-st-reject-to-edit-btn"
                  onClick={() => setShowRejectModal(true)}
                  disabled={isSubmitting}
                  className={`flex-1 h-12 rounded-lg font-medium border-2 transition-all ${
                    isSubmitting
                      ? "border-gray-200 text-gray-400 cursor-not-allowed opacity-50"
                      : "border-amber-400 text-amber-700 hover:bg-amber-50"
                  }`}
                >
                  Reject to edit
                </button>
                <button
                  id="warehouse-outward-st-device-allocation-submit-btn"
                  onClick={handleAllocateDevices}
                  disabled={isSubmitting}
                  className={`flex-1 h-12 rounded-lg font-medium transition-all ${
                    isSubmitting
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {isSubmitting ? "Allocating..." : "Allocate Devices"}
                </button>
              </div>
            </div>
          )}

        {stage === WarehouseOutwardStatus.MATERIAL_PICKING_PENDING && (
          <div className="space-y-6 mt-6">
            {/* Meta */}
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div className="space-y-1">
                <p className="text-gray-500 flex items-center gap-2">
                  <Icon
                    path={mdiWarehouse}
                    size={0.7}
                    className="text-blue-600"
                  />{" "}
                  From Warehouse
                </p>
                <p className="font-semibold text-gray-900">
                  {outward.from_warehouse_name}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-gray-500 flex items-center gap-2">
                  <Icon
                    path={mdiMapMarker}
                    size={0.7}
                    className="text-green-600"
                  />{" "}
                  Destination
                </p>
                <p className="font-semibold text-gray-900">
                  {getOutwardDestinationLabel(outward)}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-gray-500 flex items-center gap-2">
                  <Icon
                    path={mdiCalendar}
                    size={0.7}
                    className="text-purple-600"
                  />{" "}
                  Expected Date
                </p>
                <p className="font-semibold text-gray-900">
                  {outward.type === "ST" &&
                    outward.st_subtype === StockTransferSubtype.REGULAR &&
                    formatDate(outward.expected_date)}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-gray-500 flex items-center gap-2">
                  <Icon
                    path={mdiPackageVariant}
                    size={0.7}
                    className="text-orange-600"
                  />{" "}
                  Total Value
                </p>
                <p className="font-semibold text-gray-900 text-lg">
                  ₹{getTotalValue(outward).toLocaleString()}
                </p>
              </div>
            </div>

            <hr className="border-gray-200" />

            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-gray-900">
                <Icon path={mdiPackageVariant} size={0.8} />
                Confirm Picked Quantity
              </h3>
              <p className="text-sm text-gray-500">
                Please pick and place these items in the designated area, then
                enter the exact quantity picked for each item below.
              </p>

              {outward.items?.map((item) => {
                const availableQty =
                  warehouseStock[item.inventoryItemCode] ?? null;
                const value =
                  pickedQty[item.inventoryItemCode] ?? item.requestedQty;

                return (
                  <div
                    key={item.inventoryItemCode}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{item.itemName}</p>
                        <p className="text-sm text-gray-600">
                          {item.inventoryItemCode}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-sm items-end">
                      <div>
                        <p className="text-gray-500">Required Qty</p>
                        <p className="font-semibold">{item.requestedQty}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Available Now</p>
                        <p className="font-semibold">
                          {availableQty === null ? "-" : availableQty}
                        </p>
                      </div>
                      <div>
                        <label
                          htmlFor={`picked-qty-${item.inventoryItemCode}`}
                          className="text-gray-500 block mb-1"
                        >
                          Picked Qty
                        </label>
                        <input
                          id={`picked-qty-${item.inventoryItemCode}`}
                          type="number"
                          min={0}
                          value={value}
                          onChange={(e) =>
                            setPickedQty((prev) => ({
                              ...prev,
                              [item.inventoryItemCode]:
                                e.target.value === ""
                                  ? 0
                                  : Number(e.target.value),
                            }))
                          }
                          className={`w-full h-9 rounded-md border px-2 text-sm ${
                            value === item.requestedQty
                              ? "border-gray-300"
                              : "border-amber-400 bg-amber-50"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {failedPickingItems.length > 0 && (
              <div className="rounded-lg border border-red-300 bg-red-50 p-4 space-y-2">
                <p className="font-semibold text-red-700 flex items-center gap-2">
                  <Icon path={mdiAlertCircleOutline} size={0.8} />
                  {failedPickingItems.length} item
                  {failedPickingItems.length > 1 ? "s" : ""} could not be
                  confirmed
                </p>
                <ul className="text-sm text-red-700 space-y-1 max-h-48 overflow-y-auto">
                  {failedPickingItems.map((f) => (
                    <li
                      key={f.itemCode}
                      className="flex justify-between gap-3 border-b border-red-100 pb-1"
                    >
                      <span className="font-mono">{f.itemCode}</span>
                      <span className="text-right">{f.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                id="warehouse-outward-st-material-picking-cancel-btn"
                onClick={onClose}
                className="flex-1 h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                id="warehouse-outward-st-material-picking-reject-btn"
                onClick={() => setShowRejectModal(true)}
                disabled={isSubmitting}
                className={`flex-1 h-12 rounded-lg font-medium border-2 transition-all ${
                  isSubmitting
                    ? "border-gray-200 text-gray-400 cursor-not-allowed opacity-50"
                    : "border-amber-400 text-amber-700 hover:bg-amber-50"
                }`}
              >
                Reject to edit
              </button>
              <button
                id="warehouse-outward-st-material-picking-submit-btn"
                onClick={handleConfirmMaterialPicking}
                disabled={isSubmitting}
                className={`flex-1 h-12 rounded-lg font-medium transition-all ${
                  isSubmitting
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {isSubmitting ? "Confirming..." : "Confirm Picking"}
              </button>
            </div>
          </div>
        )}

        {stage === WarehouseOutwardStatus.READY_TO_DISPATCH && (
          <div className="space-y-6 mt-6">
            {/* Meta */}
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div className="space-y-1">
                <p className="text-gray-500 flex items-center gap-2">
                  <Icon
                    path={mdiWarehouse}
                    size={0.7}
                    className="text-blue-600"
                  />{" "}
                  From Warehouse
                </p>
                <p className="font-semibold text-gray-900">
                  {outward.from_warehouse_name}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-gray-500 flex items-center gap-2">
                  <Icon
                    path={mdiMapMarker}
                    size={0.7}
                    className="text-green-600"
                  />{" "}
                  Destination
                </p>
                <p className="font-semibold text-gray-900">
                  {getOutwardDestinationLabel(outward)}
                </p>
                <p className="text-xs text-gray-500">
                  {formatAddress(getOutwardDestinationAddress(outward))}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-gray-500 flex items-center gap-2">
                  <Icon
                    path={mdiCalendar}
                    size={0.7}
                    className="text-purple-600"
                  />{" "}
                  Expected Date
                </p>
                <p className="font-semibold text-gray-900">
                  {outward.type === "ST" &&
                    outward.st_subtype === StockTransferSubtype.REGULAR &&
                    formatDate(outward.expected_date)}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-gray-500 flex items-center gap-2">
                  <Icon
                    path={mdiPackageVariant}
                    size={0.7}
                    className="text-orange-600"
                  />{" "}
                  Total Value
                </p>
                <p className="font-semibold text-gray-900 text-lg">
                  ₹{getTotalValue(outward).toLocaleString()}
                </p>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Items */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-gray-900">
                <Icon
                  path={mdiPackageVariant}
                  size={0.8}
                  className="text-orange-600"
                />
                Items
              </h3>

              {outward.items?.map((item) => (
                <div
                  key={item.inventoryItemCode}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3"
                >
                  <div className="flex justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {item.itemName}
                      </p>
                      <p className="text-sm text-gray-600">
                        {item.inventoryItemCode}
                      </p>
                    </div>
                    <p className="font-bold text-green-600 text-lg">
                      ₹
                      {(
                        (item.itemPrice || 0) * item.requestedQty
                      ).toLocaleString()}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 text-sm">
                    <div>
                      <p className="text-gray-500">Ordered Qty</p>
                      <p className="font-semibold text-gray-900">
                        {item.requestedQty}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Unit Price</p>
                      <p className="font-semibold text-gray-900">
                        {item.itemPrice}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Scan Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Logistic Partner *
                </label>
                <LogisticPartnerSelect
                  id="warehouse-outward-dispatch-logistic-partner-select"
                  value={logisticPartner}
                  onChange={setLogisticPartner}
                  resetKey={open ? outward.id : ""}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Tracking ID
                </label>
                <input
                  id="warehouse-outward-dispatch-tracking-id-input"
                  type="text"
                  className="w-full mt-1 h-12 border-2 border-gray-300 rounded-lg px-3"
                  placeholder="Enter tracking ID (optional)"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                id="warehouse-outward-dispatch-cancel-btn"
                onClick={onClose}
                className="flex-1 h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                id="warehouse-outward-dispatch-submit-btn"
                onClick={handleMarkAsDispatched}
                disabled={
                  isSubmitting ||
                  (!logisticPartner.trim() &&
                    !outward.shipping_details?.logistic_partner?.trim())
                }
                className={`flex-1 h-12 rounded-lg font-medium transition-all ${
                  isSubmitting ||
                  (!logisticPartner.trim() &&
                    !outward.shipping_details?.logistic_partner?.trim())
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {isSubmitting
                  ? "Saving..."
                  : trackingId.trim()
                    ? "Mark as Dispatched"
                    : "Save & Move to Tracking ID Pending"}
              </button>
            </div>
          </div>
        )}

        {stage === WarehouseOutwardStatus.TRACKING_ID_PENDING && (
          <div className="space-y-6 mt-6">
            <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-900">
              <Icon
                path={mdiCheckCircle}
                size={0.9}
                className="text-yellow-500"
              />
              Tracking ID Pending
            </h3>

            <div className="warning-card-yellow border border-yellow-600 rounded-lg p-4">
              <p className="text-sm text-orange-700">
                Logistic partner is already set. Please enter tracking ID to
                mark this outward as dispatched.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
              <p className="flex justify-between">
                <strong className="text-gray-700">Order:</strong>
                <span className="text-gray-900 font-semibold">
                  {outward.id}
                </span>
              </p>
              <p className="flex justify-between">
                <strong className="text-gray-700">Logistic Partner:</strong>
                <span className="text-gray-900 font-semibold">
                  {logisticPartner ||
                    outward.shipping_details?.logistic_partner ||
                    "N/A"}
                </span>
              </p>
              <p className="flex justify-between">
                <strong className="text-gray-700">Destination:</strong>
                <span className="text-gray-900 font-semibold">
                  {getOutwardDestinationLabel(outward)}
                </span>
              </p>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">
                Tracking ID *
              </label>
              <input
                id="warehouse-outward-tracking-pending-input"
                type="text"
                className="w-full mt-1 h-12 border-2 border-gray-300 rounded-lg px-3"
                placeholder="Enter tracking number"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                id="warehouse-outward-tracking-pending-cancel-btn"
                onClick={onClose}
                className="flex-1 h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                id="warehouse-outward-tracking-pending-submit-btn"
                onClick={handleUpdateTrackingIdAndDispatch}
                disabled={isSubmitting || !trackingId.trim()}
                className={`flex-1 h-12 rounded-lg font-medium transition-all ${
                  isSubmitting || !trackingId.trim()
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {isSubmitting ? "Updating..." : "Mark as Dispatched"}
              </button>
            </div>
          </div>
        )}

        {stage === WarehouseOutwardStatus.DISPATCHED && (
          <div className="space-y-6 mt-6">
            <h3 className="font-semibold flex items-center gap-2 text-gray-900">
              <Icon
                path={mdiCheckCircle}
                size={0.9}
                className="text-green-600"
              />
              Delivery Details
            </h3>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-700">
                Please confirm delivery for this outward order.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
              <p className="flex justify-between">
                <strong className="text-gray-700">Order:</strong>
                <span className="text-gray-900 font-semibold">
                  {outward.id}
                </span>
              </p>
              <p className="flex justify-between">
                <strong className="text-gray-700">Destination:</strong>
                <span className="text-gray-900 font-semibold">
                  {getOutwardDestinationLabel(outward)}
                </span>
              </p>
              <p className="flex justify-between">
                <strong className="text-gray-700">Total Value:</strong>
                <span className="text-green-600 font-bold text-lg">
                  ₹{getTotalValue(outward).toLocaleString()}
                </span>
              </p>
            </div>

            {isDelhivery && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-semibold text-gray-700">
                      Delhivery status:{" "}
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {outward.shipping_details?.tracking_status_raw ||
                        outward.shipping_details?.tracking_status ||
                        "Unknown"}
                    </span>
                  </div>
                  <button
                    id="warehouse-outward-dispatched-sync-delhivery-btn"
                    onClick={handleSyncDelhivery}
                    disabled={isSubmitting || pendingDelhiverySwitch}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      isSubmitting || pendingDelhiverySwitch
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    <Icon path={mdiSync} size={0.6} />
                    {isSubmitting ? "Syncing..." : "Sync Delhivery status"}
                  </button>
                </div>
                {pendingDelhiverySwitch && (
                  <p className="text-xs text-gray-500">
                    Update the logistic partner to Delhivery first to enable
                    status sync.
                  </p>
                )}
                {outward.shipping_details?.delhivery_synced_at && (
                  <p className="text-xs text-gray-500">
                    Last checked:{" "}
                    {new Date(
                      outward.shipping_details.delhivery_synced_at,
                    ).toLocaleString()}
                  </p>
                )}
                {outward.shipping_details?.delhivery_sync_error && (
                  <p className="flex items-start gap-1.5 text-xs text-red-600">
                    <Icon
                      path={mdiAlertCircleOutline}
                      size={0.6}
                      className="shrink-0 mt-0.5"
                    />
                    {outward.shipping_details.delhivery_sync_error}
                  </p>
                )}
                {syncMessage && (
                  <p className="flex items-start gap-1.5 text-xs text-green-700">
                    <Icon
                      path={mdiCheckCircle}
                      size={0.6}
                      className="shrink-0 mt-0.5"
                    />
                    {syncMessage}
                  </p>
                )}
              </div>
            )}

            {!isInternalST && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Logistic Partner
                  </label>
                  <LogisticPartnerSelect
                    id="warehouse-outward-dispatched-logistic-partner-select"
                    value={logisticPartner}
                    onChange={setLogisticPartner}
                    resetKey={open ? outward.id : ""}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tracking ID
                  </label>
                  <input
                    id="warehouse-outward-dispatched-tracking-id-input"
                    type="text"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    placeholder="Enter tracking ID"
                    className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Remarks
              </label>
              <textarea
                id="warehouse-outward-dispatched-remarks-textarea"
                value={deliveryRemarks}
                onChange={(e) => setDeliveryRemarks(e.target.value)}
                rows={2}
                placeholder="Add any remarks (optional)"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {!isInternalST && (
              <div
                id="warehouse-outward-dispatched-attachments-wrapper"
                className="space-y-2"
              >
                <label className="block text-sm font-medium text-gray-700">
                  Delivery Attachments
                </label>

                <InwardAttachmentUpload
                  accept="application/pdf,image/jpeg,image/png"
                  maxSizeMB={10}
                  files={deliveryAttachments}
                  multiple
                  onChange={setDeliveryAttachments}
                />
              </div>
            )}

            {blockManualDelivery && (
              <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                <Icon
                  path={mdiAlertCircleOutline}
                  size={0.6}
                  className="shrink-0 mt-0.5"
                />
                {pendingDelhiverySwitch
                  ? "Switching this shipment to Delhivery. Update the logistic partner — Delhivery shipments are marked delivered only after the POD is synced from Delhivery, not manually."
                  : "This Delhivery shipment is not marked DELIVERED yet. Sync the Delhivery status above — it will be marked delivered automatically once Delhivery reports delivery."}
              </p>
            )}

            <div className="flex gap-3 pt-4">
              <button
                id="warehouse-outward-dispatched-cancel-btn"
                onClick={onClose}
                className="flex-1 h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              {pendingDelhiverySwitch ? (
                <button
                  id="warehouse-outward-dispatched-update-logistic-btn"
                  onClick={handleUpdateLogisticPartner}
                  disabled={isSubmitting || !logisticPartner.trim()}
                  className={`flex-1 h-12 rounded-lg font-medium transition-all shadow-lg ${
                    isSubmitting || !logisticPartner.trim()
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {isSubmitting ? "Updating..." : "Update Logistic Partner"}
                </button>
              ) : (
                <button
                  id="warehouse-outward-dispatched-mark-delivered-btn"
                  onClick={handleMarkAsDelivered}
                  disabled={isSubmitting || blockManualDelivery}
                  className={`flex-1 h-12 rounded-lg font-medium transition-all shadow-lg ${
                    isSubmitting || blockManualDelivery
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {isSubmitting ? "Delivering..." : "Mark as Delivered"}
                </button>
              )}
            </div>
          </div>
        )}

        {stage === WarehouseOutwardStatus.DELIVERED && (
          <div className="space-y-6 mt-6">
            <h3 className="font-semibold flex items-center gap-2 text-gray-900">
              <Icon
                path={mdiCheckCircle}
                size={0.9}
                className="text-green-600"
              />
              Delivery Summary
            </h3>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
              <p className="flex justify-between">
                <strong className="text-gray-700">Order:</strong>
                <span className="text-gray-900 font-semibold">
                  {outward.id}
                </span>
              </p>
              <p className="flex justify-between">
                <strong className="text-gray-700">Logistic Partner:</strong>
                <span className="text-gray-900 font-semibold">
                  {outward.shipping_details?.logistic_partner || "N/A"}
                </span>
              </p>
              <p className="flex justify-between">
                <strong className="text-gray-700">Tracking ID:</strong>
                <span className="text-gray-900 font-semibold">
                  {outward.shipping_details?.tracking_id || "N/A"}
                </span>
              </p>
              {outward.delivered_date && (
                <p className="flex justify-between">
                  <strong className="text-gray-700">Delivered On:</strong>
                  <span className="text-gray-900 font-semibold">
                    {new Date(outward.delivered_date).toLocaleString()}
                  </span>
                </p>
              )}
              {outward.shipping_details?.tracking_status && (
                <p className="flex justify-between">
                  <strong className="text-gray-700">Tracking Status:</strong>
                  <span className="text-gray-900 font-semibold">
                    {outward.shipping_details.tracking_status}
                  </span>
                </p>
              )}
            </div>

            {outward.attachments && outward.attachments.length > 0 && (
              <div className="space-y-4">
                {Object.entries(
                  outward.attachments.reduce(
                    (acc, file) => {
                      const category = file.category || "Other Attachments";
                      if (!acc[category]) acc[category] = [];
                      acc[category].push(file);
                      return acc;
                    },
                    {} as Record<string, typeof outward.attachments>,
                  ),
                ).map(([category, files]) => (
                  <div key={category} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {category}
                    </label>
                    <div className="grid grid-cols-1 gap-2">
                      {files.map((file, idx) => (
                        <div
                          key={file.id || idx}
                          className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3 overflow-hidden">
                            <Icon
                              path={mdiAttachment}
                              size={0.7}
                              className="text-gray-400 shrink-0"
                            />
                            <span className="text-sm text-gray-600 truncate">
                              {`Attachment ${idx + 1}`}
                            </span>
                          </div>
                          <a
                            id={`warehouse-outward-delivered-view-attachment-${file.id || idx}`}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-md transition-colors shrink-0"
                          >
                            <Icon path={mdiEye} size={0.6} />
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="pt-4">
              <button
                id="warehouse-outward-delivered-close-btn"
                onClick={onClose}
                className="w-full h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
        {/* Close Button */}
        <button
          id="warehouse-outward-stage-modal-close-icon-btn"
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <Icon
            path={mdiClose}
            size={0.9}
            className="text-gray-500 hover:text-gray-700"
          />
        </button>
      </div>

      {showReplacementModal && (
        <ReplacementItemsModal
          outward={outward}
          fromWarehouse={outward.from_warehouse_id}
          availableItemCodes={[]}
          onClose={() => setShowReplacementModal(false)}
          onSaved={(savedItems) => {
            setOutward((prev: any) => ({
              ...prev,
              items: savedItems.map((i) => ({
                inventoryItemCode: i.item_code,
                itemName: i.item_name,
                requestedQty: i.qty,
                itemPrice: i.price,
                subCategory: i.subCategory,
              })),
            }));
            setShowReplacementModal(false);
          }}
        />
      )}

      {/* Device Allocation Modal */}
      <DeviceAllocationModal
        open={deviceAllocationOpen}
        from_warehouse_id={outward.from_warehouse_id || selectedFromWarehouse}
        item_code={selectedItemCode || ""}
        requested_qty={selectedItemQty}
        onClose={() => {
          setDeviceAllocationOpen(false);
          setSelectedItemCode("");
          setSelectedItemQty(0);
        }}
        onSave={(devices) => {
          if (selectedItemCode) {
            setItemDevices((prev) => ({
              ...prev,
              [selectedItemCode]: devices,
            }));
          }
        }}
      />

      <RejectSTModal
        open={showRejectModal}
        selectedOutward={outward}
        onClose={() => setShowRejectModal(false)}
        onSuccess={() => {
          setShowRejectModal(false);
          onSuccess?.();
          onClose();
        }}
      />

      {isSubmitting && <LoadingWidget />}
    </>
  );
};

export default OutwardStageModal;
